<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { AdminRecord } from '@/types/record'
import type { FormFieldDef, PageDefinition, SelectOption } from '@/types/page'
import { usePrototypeStore } from '@/stores/prototype'
import { useSessionCacheStore } from '@/stores/session-cache'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { formatDateTimeInput, todayDateInput } from '@/foundation/time'
import ProField from './ProField.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  open: boolean
  mode: 'create' | 'edit'
  definition: PageDefinition
  record?: AdminRecord | null
  preset?: Record<string, unknown> | null
}>()

const emit = defineEmits<{
  close: []
  saved: [record: AdminRecord]
}>()

const store = usePrototypeStore()
const cache = useSessionCacheStore()
const form = reactive<Record<string, unknown>>({})
const errors = reactive<Record<string, string>>({})
const saving = ref(false)
const snapshot = ref('')
const bodyRef = ref<HTMLElement | null>(null)

const eligibleFields = computed(() => (props.definition.formFields ?? []).filter(field => {
  if (props.mode === 'edit' && field.createOnly) return false
  return true
}))

const sections = computed(() => {
  const fields = eligibleFields.value
  if (fields.length <= 6) return [{ title: '基本信息', fields }]
  const first = fields.slice(0, Math.ceil(fields.length / 3))
  const second = fields.slice(first.length, first.length + Math.ceil(fields.length / 3))
  const third = fields.slice(first.length + second.length)
  return [
    { title: '基本信息', fields: first },
    { title: '业务设置', fields: second },
    { title: '补充信息', fields: third },
  ].filter(section => section.fields.length)
})

const dirty = computed(() => JSON.stringify(serialize()) !== snapshot.value)

function defaultDate(): string {
  return todayDateInput()
}

function defaultFor(field: FormFieldDef): unknown {
  if (field.defaultValue !== undefined) return Array.isArray(field.defaultValue) ? [...field.defaultValue] : field.defaultValue
  if (field.type === 'multiselect' || field.type === 'tags') return []
  if (field.type === 'switch') return false
  if (field.type === 'number' || field.type === 'money') return field.type === 'number' ? undefined : undefined
  if (field.type === 'date' && ['start_date', 'end_date'].includes(field.key)) return defaultDate()
  return ''
}

function initialize(): void {
  Object.keys(form).forEach(key => delete form[key])
  Object.keys(errors).forEach(key => delete errors[key])
  for (const field of eligibleFields.value) {
    const raw = props.record?.[field.key]
    const preset = props.mode === 'create' ? props.preset?.[field.key] : undefined
    let value: unknown
    if (props.mode === 'edit' && raw !== undefined && raw !== null) {
      value = Array.isArray(raw) ? [...raw] : raw
    } else if (preset !== undefined && preset !== null) {
      value = Array.isArray(preset) ? [...preset] : preset
    } else {
      value = defaultFor(field)
    }
    // Older prototype rows may persist the human label while the select
    // options use stable codes (for example a managed link target). Normalise
    // the label back to its option value so editing never shows an empty
    // control or silently clears the field.
    if (['select', 'region', 'user'].includes(field.type) && typeof value === 'string' && value) {
      const options = fieldOptions(field)
      const exact = options.find(option => String(option.value) === value)
      const byLabel = options.find(option => String(option.label) === value)
      if (!exact && byLabel) value = byLabel.value
    }
    if (field.type === 'datetime' && typeof value === 'string' && value) value = formatDateTimeInput(value)
    if (field.type === 'money' && typeof value === 'number') value = value / 100
    if (field.type === 'money' && typeof value === 'string' && value) value = Number(value) / 100
    form[field.key] = value
  }
  snapshot.value = JSON.stringify(serialize())
}

function serialize(): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const field of eligibleFields.value) {
    let value = form[field.key]
    if (field.type === 'money') value = Math.round(Number(value || 0) * 100)
    if (field.readonlyOnEdit && props.mode === 'edit') continue
    payload[field.key] = value
  }
  return JSON.parse(JSON.stringify(payload)) as Record<string, unknown>
}

function fieldOptions(field: FormFieldDef): SelectOption[] {
  if (field.optionSource) return store.options(props.definition, field.optionSource)
  return field.options ?? []
}

function validateField(field: FormFieldDef): string {
  const value = form[field.key]
  const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
  if (field.required && empty) return `请填写${field.label}`
  if (empty) return ''
  if (field.type === 'file' || field.type === 'image') return ''
  if (field.type === 'number' || field.type === 'money') {
    const number = Number(value)
    if (!Number.isFinite(number)) return `${field.label}必须是数字`
    if (field.min !== undefined && number < field.min) return `${field.label}不能小于 ${field.min}`
    if (field.max !== undefined && number > field.max) return `${field.label}不能大于 ${field.max}`
  }
  if (field.pattern) {
    try {
      if (!new RegExp(field.pattern).test(String(value))) return field.patternMessage ?? `${field.label}格式不正确`
    } catch {
      return ''
    }
  }
  if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) return '请输入有效邮箱'
  if (field.type === 'phone' && !/^1[3-9]\d{9}$/.test(String(value))) return '请输入 11 位有效手机号'
  if (field.maxLength && String(value).length > field.maxLength) return `${field.label}不能超过 ${field.maxLength} 个字符`
  return ''
}

function validate(): boolean {
  Object.keys(errors).forEach(key => delete errors[key])
  let valid = true
  let firstKey = ''
  for (const field of eligibleFields.value) {
    const message = validateField(field)
    if (message) {
      errors[field.key] = message
      if (!firstKey) firstKey = field.key
      valid = false
    }
  }
  if (!valid && firstKey) {
    nextTick(() => {
      const element = bodyRef.value?.querySelector(`[data-field="${firstKey}"]`) as HTMLElement | null
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const input = element?.querySelector('input, textarea, button') as HTMLElement | null
      input?.focus()
    })
  }
  return valid
}

async function submit(): Promise<void> {
  if (saving.value) return
  if (!validate()) {
    ElMessage.warning('请先修正标注的字段')
    return
  }
  saving.value = true
  const payload = serialize()
  if (props.mode === 'edit') {
    payload.row_version = props.record?.row_version
    payload.__rowVersion = props.record?.row_version
  }
  try {
    const result = await request(
      () => props.mode === 'create'
        ? store.createRecord(props.definition, payload)
        : store.updateRecord(props.definition, props.record?.id, payload),
      { write: true, delay: 320, useSimulation: true },
    )
    saving.value = false
    if (!result.ok) {
      const fieldErrors = (result as { fieldErrors?: Record<string, string> }).fieldErrors
      if (fieldErrors) Object.assign(errors, fieldErrors)
      if (result.message.includes('其他操作')) {
        errors['__form'] = '数据版本已变化，请关闭抽屉刷新后重试'
      }
      ElMessage.error(result.message)
      return
    }
    snapshot.value = JSON.stringify(serialize())
    cache.cachePage(props.definition.id, {})
    ElMessage.success(result.message)
    emit('saved', (result.item ?? props.record ?? {}) as AdminRecord)
    emit('close')
  } catch (caught) {
    saving.value = false
    if (caught instanceof ApiError && caught.fieldErrors) Object.assign(errors, caught.fieldErrors)
    errors['__form'] = caught instanceof Error ? caught.message : '保存失败，请重试'
    ElMessage.error(errors['__form'])
  }
}

function settle(done?: () => void): void {
  if (done) done()
  emit('close')
}

async function requestClose(done?: () => void): Promise<void> {
  if (!dirty.value) {
    settle(done)
    return
  }
  try {
    await ElMessageBox.confirm('当前表单有未保存的修改，确认关闭并放弃这些内容？', '放弃未保存的修改', {
      type: 'warning',
      confirmButtonText: '放弃修改',
      cancelButtonText: '继续编辑',
      distinguishCancelAndClose: true,
    })
    settle(done)
  } catch {
    // Keep editing.
  }
}

function beforeClose(done: () => void): void {
  void requestClose(done)
}

function beforeUnload(event: BeforeUnloadEvent): void {
  if (!props.open || !dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onBeforeRouteLeave(async () => {
  if (!props.open || !dirty.value) return true
  try {
    await ElMessageBox.confirm('当前表单有未保存的修改，离开页面会放弃这些内容。', '放弃未保存的修改', {
      type: 'warning',
      confirmButtonText: '放弃并离开',
      cancelButtonText: '继续编辑',
      distinguishCancelAndClose: true,
    })
    return true
  } catch {
    return false
  }
})

onMounted(() => window.addEventListener('beforeunload', beforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))

function onKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
    event.preventDefault()
    void submit()
  }
}

watch(() => props.open, open => {
  if (open) {
    initialize()
    nextTick(() => {
      const input = bodyRef.value?.querySelector('input, textarea') as HTMLElement | null
      input?.focus()
    })
  }
}, { immediate: true })

watch(() => props.record, () => {
  if (props.open) initialize()
})
</script>

<template>
  <el-drawer
    :model-value="open"
    :size="'560px'"
    :with-header="false"
    :close-on-click-modal="false"
    destroy-on-close
    class="form-drawer"
    :before-close="beforeClose"
    @keydown="onKeydown"
  >
    <div class="side-form">
      <header class="drawer-head form-drawer__head">
        <div class="drawer-head__text">
          <h2 class="drawer-title">{{ mode === 'create' ? definition.createLabel || `新增${definition.title}` : `编辑${definition.title}` }}</h2>
          <p class="drawer-sub">{{ mode === 'create' ? '表单字段与列表共用同一份业务定义，保存后立即反映到列表和汇总。' : `正在编辑：${record?.name || record?.title || record?.question || record?.order_no || record?.id || ''}` }}</p>
        </div>
        <button type="button" class="icon-button" aria-label="关闭表单" @click="requestClose()"><AppIcon name="close" :size="18" /></button>
      </header>
      <div ref="bodyRef" class="drawer-body form-drawer__body">
        <div v-if="errors.__form" class="info-banner info-banner--danger">{{ errors.__form }}</div>
        <section v-for="(section, index) in sections" :key="section.title" class="form-section">
          <h3 class="form-section__title">{{ section.title }}</h3>
          <div class="form-grid">
            <div v-for="field in section.fields" :key="field.key" :data-field="field.key" :class="{ 'span-2': field.full }">
              <ProField
                v-model="form[field.key]"
                :field="field"
                :options="fieldOptions(field)"
                :error="errors[field.key]"
                :disabled="saving || (mode === 'edit' && field.readonlyOnEdit)"
                @blur="errors[field.key] = validateField(field)"
              />
            </div>
          </div>
          <p v-if="index === 0 && definition.notice" class="form-tip">{{ definition.notice }}</p>
        </section>
      </div>
      <footer class="drawer-foot form-drawer__foot">
        <span class="drawer-foot__hint">按 ⌘/Ctrl + S 保存 · 关闭前会提示未保存内容</span>
        <button type="button" class="btn" :disabled="saving" @click="requestClose()">取消</button>
        <button type="button" class="btn btn--primary" :disabled="saving" @click="submit">
          <span v-if="saving" class="button-spinner" aria-hidden="true"></span>
          {{ saving ? '保存中…' : mode === 'create' ? '创建并保存' : '保存修改' }}
        </button>
      </footer>
    </div>
  </el-drawer>
</template>

<style scoped>
.side-form { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.form-drawer__head { padding: 18px 22px 14px; border-bottom: 1px solid var(--c-divider); }
.form-drawer__body { padding: 18px 22px 24px; overflow: auto; flex: 1; }
.form-drawer__foot { flex: 0 0 auto; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
</style>
